import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import type { ImportedEmbeddedContentBlockAttsInterface } from '~/types/AppData/Blocks/ImportedEmbeddedContentInterface';
import Image from '~/components/Shared/Image/Image';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { AppDataContext } from '~/contexts/AppDataContext';
import {
  getCSSSizeSmText,
  getCSSSizeMdText,
  getCSSSizeLgText,
  getCSSColorText,
} from '~/utilities/getCSS';

const titleCSS = cva(
  'text-black max-w-full block my-5 group-hover:text-secondary',
  {
    variants: {
      sizeSm: getCSSSizeSmText(),
      sizeMd: getCSSSizeMdText(),
      sizeLg: getCSSSizeLgText(),
    },
  }
);

const descriptionCSS = cva('my-4', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

export default component$(
  ({
    embeddedContentDescription,
    embeddedContentTitle,
    embeddedContentUrl,
    imageFileExtension,
    imagePath,
  }: ImportedEmbeddedContentBlockAttsInterface): JSX.Element => {
    const {
      themeComponents: { verticalCard },
    } = useContext(AppDataContext);

    // default imported embedded content image sizes
    const imageWidths = { small: 360, medium: 360, large: 540 };

    return (
      <article class='shadow-[0_0_0_1px] shadow-lightGrey group'>
        <a href={embeddedContentUrl}>
          <Image
            path={imagePath}
            imageWidths={imageWidths}
            fileExtension={imageFileExtension}
            loading='lazy'
          />
          <div class='p-4'>
            <header>
              <h3
                class={titleCSS(
                  getFontSizeObject(verticalCard, 'titleFontSize')
                )}
              >
                {embeddedContentTitle}
              </h3>
            </header>
            <p
              class={descriptionCSS({
                color: verticalCard.descriptionColor.value,
                ...getFontSizeObject(verticalCard, 'descriptionFontSize'),
              })}
            >
              {embeddedContentDescription}
            </p>
          </div>
        </a>
      </article>
    );
  }
);
